import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, formatApiError } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { AlertTriangle, Bell, Clock, TrendingUp, Search, ChevronRight, Zap, Trash2 } from "lucide-react";

const SLA_TONE = {
  overdue: { bg: "bg-red-100",    fg: "text-red-700",    label: "Overdue" },
  warn:    { bg: "bg-amber-100",  fg: "text-amber-700",  label: "SLA at risk" },
  ok:      { bg: "bg-emerald-50", fg: "text-emerald-700", label: "On track" },
};

/**
 * AdminOnboardingPriority — master queue sorted by urgency descending.
 * Overdue records outrank SLA-warn which outranks on-track; active
 * rework + open corrections nudge scores upward. Rows the current user
 * hasn't opened since the last server-side change get a gold UPDATE
 * badge that clears when they open the detail view.
 */
export default function AdminOnboardingPriority() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [slaFilter, setSlaFilter] = useState("all");
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/onboarding/priority");
      setRecords(data?.records || []);
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to load priority queue");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const deleteRecord = async (r) => {
    if (!window.confirm(`Delete onboarding for "${r.dealer_name || 'this dealer'}"? This hard-deletes the record + history. Cannot be undone (use to clear demo/training records).`)) return;
    try {
      await api.delete(`/onboarding/${r.id}`);
      toast.success(`Deleted · ${r.dealer_name || r.id}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const openRow = async (r) => {
    // Fire-and-forget mark-viewed so the badge clears on return.
    api.post(`/admin/onboarding/${r.id}/mark-viewed`).catch(() => {});
    navigate(`/onboarding/${r.id}`);
  };

  const filtered = useMemo(() => {
    let list = records;
    if (slaFilter !== "all") list = list.filter((r) => r.sla_status === slaFilter);
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      list = list.filter((r) =>
        (r.dealer_name || "").toLowerCase().includes(needle) ||
        (r.agency || "").toLowerCase().includes(needle) ||
        (r.signing_agent_name || "").toLowerCase().includes(needle),
      );
    }
    return list;
  }, [records, q, slaFilter]);

  const stats = useMemo(() => {
    const s = { total: records.length, overdue: 0, warn: 0, updates: 0 };
    for (const r of records) {
      if (r.sla_status === "overdue") s.overdue += 1;
      if (r.sla_status === "warn") s.warn += 1;
      if (r.has_update) s.updates += 1;
    }
    return s;
  }, [records]);

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />
      <main className="max-w-[1400px] mx-auto px-6 py-10">
        <div className="mb-10">
          <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
            Onboarding
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-black text-[#0B132B] mt-2 tracking-tight leading-none">
            Priority queue
          </h1>
          <p className="text-sm text-[#6C757D] mt-3 max-w-xl leading-relaxed">
            Every active onboarding sorted by urgency. Overdue SLAs surface first — new
            activity since your last visit gets a gold UPDATE badge.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8" data-testid="admin-priority-stats">
          <StatCard label="Total active" value={stats.total} icon={<TrendingUp size={16} />} tone="neutral" />
          <StatCard label="Overdue" value={stats.overdue} icon={<AlertTriangle size={16} />} tone="danger" onClick={() => setSlaFilter(slaFilter === "overdue" ? "all" : "overdue")} active={slaFilter === "overdue"} />
          <StatCard label="SLA at risk" value={stats.warn} icon={<Clock size={16} />} tone="warn" onClick={() => setSlaFilter(slaFilter === "warn" ? "all" : "warn")} active={slaFilter === "warn"} />
          <StatCard label="Unread updates" value={stats.updates} icon={<Bell size={16} />} tone="gold" />
        </div>

        <Card className="bg-white border border-[#E9ECEF] rounded-sm" data-testid="admin-priority-queue">
          <div className="p-4 border-b border-[#E9ECEF] flex items-center justify-between gap-4">
            <div className="relative w-96 max-w-full">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6C757D]" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Filter dealer, agency, or agent…"
                className="pl-9 h-9 rounded-sm text-sm"
                data-testid="admin-priority-search"
              />
            </div>
            <div className="text-xs text-[#6C757D]">
              {loading ? "Loading…" : `${filtered.length} of ${records.length}`}
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-[#F8F9FA] text-[10px] uppercase tracking-[0.16em] text-[#6C757D]">
              <tr>
                <th className="text-left p-4">Urgency</th>
                <th className="text-left p-4">Dealer</th>
                <th className="text-left p-4">Stage</th>
                <th className="text-left p-4">SLA</th>
                <th className="text-left p-4">Days in stage</th>
                <th className="text-left p-4">Agency / Agent</th>
                <th className="text-right p-4"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const tone = SLA_TONE[r.sla_status] || SLA_TONE.ok;
                return (
                  <tr
                    key={r.id}
                    onClick={() => openRow(r)}
                    className="border-t border-[#E9ECEF] hover:bg-[#F8F9FA] cursor-pointer"
                    data-testid={`admin-priority-row-${r.id}`}
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        {r.urgency >= 500 && <Zap size={14} className="text-red-500" />}
                        <span className="text-xs font-mono font-bold text-[#6C757D]">{r.urgency}</span>
                        {r.has_update && (
                          <span
                            className="text-[9px] uppercase tracking-[0.2em] font-bold px-1.5 py-0.5 rounded-sm bg-[#D4AF37] text-[#0B132B]"
                            data-testid={`admin-priority-badge-update-${r.id}`}
                          >
                            Update
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-4 font-semibold text-[#0B132B]">
                      {r.dealer_name || "—"}
                      {r.workflow_type === "change_request" && (
                        <span className="ml-2 text-[9px] uppercase tracking-[0.18em] font-bold px-1.5 py-0.5 rounded-sm bg-blue-100 text-blue-700">
                          Change request
                        </span>
                      )}
                    </td>
                    <td className="p-4 text-[#6C757D] capitalize">{(r.status || "").replace(/_/g, " ")}</td>
                    <td className="p-4">
                      <span className={`text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-sm ${tone.bg} ${tone.fg}`}>
                        {tone.label}
                      </span>
                    </td>
                    <td className="p-4 text-xs font-mono text-[#6C757D]">{r.days_in_stage ?? "—"}</td>
                    <td className="p-4 text-xs text-[#6C757D]">
                      <div className="text-[#0B132B]">{r.agency || "—"}</div>
                      <div>{r.signing_agent_name || "—"}</div>
                    </td>
                    <td className="p-4 text-right">
                      <div className="inline-flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 w-8 p-0 rounded-sm border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300"
                          onClick={(e) => { e.stopPropagation(); e.preventDefault(); deleteRecord(r); }}
                          data-testid={`admin-priority-delete-${r.id}`}
                          title="Delete onboarding (clears demo/training records)"
                        >
                          <Trash2 size={12} />
                        </Button>
                        <ChevronRight size={14} className="text-[#6C757D]" />
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-10 text-center text-[#6C757D] italic">
                    Nothing matches. Try clearing filters or the search box.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Card>
      </main>
    </div>
  );
}

function StatCard({ label, value, icon, tone, onClick, active }) {
  const toneCls = {
    neutral: "border-[#E9ECEF]",
    warn: "border-amber-300",
    danger: "border-red-300",
    gold: "border-[#D4AF37]",
  }[tone] || "border-[#E9ECEF]";
  const iconTone = {
    neutral: "text-[#0B132B]",
    warn: "text-amber-500",
    danger: "text-red-500",
    gold: "text-[#D4AF37]",
  }[tone] || "text-[#0B132B]";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className={`text-left bg-white border rounded-sm p-5 transition-colors ${toneCls} ${
        active ? "ring-2 ring-[#0B132B]/20" : ""
      } ${onClick ? "hover:border-[#0B132B]/40 cursor-pointer" : "cursor-default"}`}
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#6C757D]">{label}</div>
        <div className={iconTone}>{icon}</div>
      </div>
      <div className="mt-2 text-3xl font-black text-[#0B132B] tabular-nums leading-none">{value}</div>
    </button>
  );
}
